import {
  ClaimResponseDeadlineExtensionPreviewDTO,
  ExtendResponseDeadlineClaimDTO,
  ExtendResponseDeadlineClaimDTOReasonTypeEnum,
} from '@reposit/api-client';
import moment from 'moment';
import numeral from 'numeral';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import {
  formatDate,
  formatEnum,
  RESPONSE_DEADLINE_EXTENSION_REASON_LABELS,
  RESPONSE_DEADLINE_EXTENSION_REASON_PLACEHOLDERS,
} from '../../constants/claim-response-deadline-extension';
import { NumberFormat } from '../../constants/number-formats';
import { FLASH_MESSAGE_TIMEOUT, useFlashMessage } from '../../containers/FlashMessage';
import { EXTEND_RESPONSE_DEADLINE_MODAL_STORE_KEY } from '../../redux/claim/claim.actions';
import Button from '../Button';
import FlashMessage from '../FlashMessage';
import Select from '../FormFields/Select';
import TextArea from '../FormFields/TextArea';
import Modal from '../Modal';
import { Header3, P2, P3 } from '../Typography';

interface ExtendResponseDeadlineModalProps {
  onDismiss: () => void;
  // Called without a date when the modal opens, to load the current deadline and the suggested new one.
  // Rejects with the reason the deadline cannot be extended.
  onPreview: (responseDeadline?: string) => Promise<ClaimResponseDeadlineExtensionPreviewDTO>;
  onConfirm: (values: ExtendResponseDeadlineClaimDTO) => Promise<void>;
}

const REASON_TYPES = Object.values(ExtendResponseDeadlineClaimDTOReasonTypeEnum);
const MAX_REASON_LENGTH = 1000;
const MODAL_MAX_WIDTH = '1080px';
const MODAL_HEIGHT = '760px';

const FixedModalLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const ScrollableContent = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding-right: 10px;
`;

const SectionDivider = styled.div<{ deletion?: boolean }>`
  align-items: center;
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
  margin-top: 4px;

  &::before,
  &::after {
    background: ${(props) => (props.deletion ? props.theme.colors.negative : '#F5A623')};
    content: '';
    flex: 1;
    height: 1px;
    opacity: 0.4;
  }
`;

const SectionDividerText = styled.span<{ deletion?: boolean }>`
  color: ${(props) => (props.deletion ? props.theme.colors.negative : '#B87A00')};
  flex-shrink: 0;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${(props) => props.theme.colors.dividerLight};
  margin: 20px 0;
`;

const SectionTitle = styled.p`
  color: ${(props) => props.theme.colors.body};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 4px;
`;

const SectionText = styled.p`
  color: ${(props) => props.theme.colors.tertiaryBrand};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 13px;
  margin: 0 0 16px;
`;

const DateChangeRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const CurrentDateValue = styled.span`
  color: ${(props) => props.theme.colors.body};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 15px;
  font-weight: 700;
`;

const DateChangeArrow = styled.span`
  color: ${(props) => props.theme.colors.tertiaryBrand};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 18px;
  font-weight: 700;
`;

const DateInputWrapper = styled.div`
  border: 1px solid ${(props) => props.theme.colors.inactive};
  border-radius: 4px;
  display: flex;
  max-width: 260px;
  overflow: hidden;

  &:focus-within {
    border-color: ${(props) => props.theme.colors.primaryBrand};
  }
`;

const VisuallyHiddenLabel = styled.label`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const DateInput = styled.input`
  border: none;
  color: ${(props) => props.theme.colors.body};
  flex: 1;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 15px;
  outline: none;
  padding: 10px 12px;
  width: 100%;

  &:disabled {
    background: ${(props) => props.theme.colors.bgAccent};
    color: ${(props) => props.theme.colors.disabled};
  }
`;

const ValidationError = styled(P3)`
  color: ${(props) => props.theme.colors.negative};
  margin-bottom: 0;
  margin-top: 6px;
`;

const FieldGroup = styled.div`
  margin-top: 16px;
`;

const ActionBar = styled.div`
  display: flex;
  gap: 10px;
  justify-content: space-between;
  padding-top: 16px;

  button {
    margin: 0;
  }
`;

const DisabledActionTooltip = styled.span`
  display: inline-flex;
`;

const ItemSummary = styled.div`
  background: #fff;
  border: 1px solid ${(props) => props.theme.colors.dividerLight};
  border-radius: 8px;
  margin-bottom: 10px;
  overflow: hidden;
  padding: 22px 14px 12px;
  position: relative;
`;

const CardTypePill = styled.span<{ deletion?: boolean }>`
  background: ${(props) => (props.deletion ? '#FFF1F1' : props.theme.colors.bgHoverAccent)};
  border-bottom-right-radius: 4px;
  color: ${(props) => (props.deletion ? props.theme.colors.negative : props.theme.colors.tertiaryBrand)};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 10px;
  font-weight: 700;
  left: 0;
  letter-spacing: 0.07em;
  padding: 3px 8px;
  position: absolute;
  text-transform: uppercase;
  top: 0;
`;

const ItemSummaryTopRow = styled.div`
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
`;

const ItemSummaryLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ItemSummaryName = styled.span<{ deleted?: boolean }>`
  color: ${(props) => props.theme.colors.body};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 15px;
  font-weight: 700;
  opacity: ${(props) => (props.deleted ? 0.5 : 1)};
  text-decoration: ${(props) => (props.deleted ? 'line-through' : 'none')};
`;

const ItemSummaryAmount = styled.span<{ deleted?: boolean }>`
  color: ${(props) => props.theme.colors.body};
  flex-shrink: 0;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 15px;
  font-weight: 700;
  margin-left: 12px;
  opacity: ${(props) => (props.deleted ? 0.5 : 1)};
  text-decoration: ${(props) => (props.deleted ? 'line-through' : 'none')};
`;

const ItemSummarySubRow = styled.div`
  color: ${(props) => props.theme.colors.placeholder};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 12px;
  margin-top: 4px;
  white-space: pre-wrap;
`;

const PreviousValue = styled.span`
  color: ${(props) => props.theme.colors.placeholder};
  text-decoration: line-through;
`;

const ChangeArrow = styled.span`
  color: ${(props) => props.theme.colors.placeholder};
  margin: 0 6px;
`;

const renderChangedValue = (oldValue: React.ReactNode, newValue: React.ReactNode) => (
  <>
    <PreviousValue>{oldValue}</PreviousValue>
    <ChangeArrow>→</ChangeArrow>
    {newValue}
  </>
);

const renderSummaryCard = (
  pill: string,
  name: React.ReactNode,
  subRow?: React.ReactNode,
  options: { deleted?: boolean; amount?: React.ReactNode } = {},
) => (
  <ItemSummary>
    <CardTypePill deletion={options.deleted}>{pill}</CardTypePill>
    <ItemSummaryTopRow>
      <ItemSummaryLeft>
        <ItemSummaryName deleted={options.deleted}>{name}</ItemSummaryName>
        {subRow ? <ItemSummarySubRow>{subRow}</ItemSummarySubRow> : null}
      </ItemSummaryLeft>
      {options.amount ? <ItemSummaryAmount deleted={options.deleted}>{options.amount}</ItemSummaryAmount> : null}
    </ItemSummaryTopRow>
  </ItemSummary>
);

const toDateInputValue = (date: Date | string) => moment(date).format('YYYY-MM-DD');
const fmt = (pence: number) => `£${numeral(pence / 100).format(NumberFormat.THOUSANDS_PENCE)}`;

// Only the tenants' deadline can be extended.
const AWAITING_TENANT_RESPONSE = 'Awaiting tenant response';

const proposalLabel = (preview: ClaimResponseDeadlineExtensionPreviewDTO) => `${formatEnum(preview.proposalType)} proposal`;

export const ExtendResponseDeadlineModal: React.FC<ExtendResponseDeadlineModalProps> = ({ onDismiss, onPreview, onConfirm }) => {
  const [current, setCurrent] = useState<ClaimResponseDeadlineExtensionPreviewDTO | undefined>();
  const [preview, setPreview] = useState<ClaimResponseDeadlineExtensionPreviewDTO | undefined>();
  const [responseDeadline, setResponseDeadline] = useState('');
  const [reasonType, setReasonType] = useState<ExtendResponseDeadlineClaimDTOReasonTypeEnum | ''>('');
  const [reason, setReason] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [loadError, setLoadError] = useState<string | undefined>();
  const [flashMessagePayload, dismissFlashMessage] = useFlashMessage([EXTEND_RESPONSE_DEADLINE_MODAL_STORE_KEY]);
  const [isBusy, setIsBusy] = useState(false);
  // A confirmed extension closes the modal before its request settles.
  const isMounted = useRef(true);
  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );
  async function whileBusy<T>(request: Promise<T>): Promise<T> {
    setIsBusy(true);
    try {
      return await request;
    } finally {
      if (isMounted.current) setIsBusy(false);
    }
  }

  useEffect(() => {
    // The first preview supplies the current deadline and the suggested new one (a week on).
    whileBusy(onPreview())
      .then((result) => {
        setCurrent(result);
        setResponseDeadline(toDateInputValue(result.responseDeadline));
      })
      .catch((error: unknown) => {
        // Keep the reason on screen: a flash message disappears, and nothing else in the form can be used.
        dismissFlashMessage();
        setLoadError(String(error));
      });
    // Load once when the modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parsedDeadline = moment(responseDeadline, 'YYYY-MM-DD', true);
  const dateError = !parsedDeadline.isValid()
    ? 'Choose a new deadline.'
    : current && !parsedDeadline.isAfter(moment(current.currentResponseDeadline), 'day')
      ? 'Choose a date after the current deadline.'
      : parsedDeadline.isBefore(moment().startOf('day'), 'day')
        ? 'Choose today or a future date.'
        : undefined;
  const reasonTypeError = reasonType ? undefined : 'Select a reason.';
  const trimmedReason = reason.trim();
  const reasonError = !trimmedReason
    ? 'Add the details of why the deadline is being extended.'
    : trimmedReason.length > MAX_REASON_LENGTH
      ? `Keep the details to ${MAX_REASON_LENGTH} characters or fewer.`
      : undefined;
  const firstError = dateError || reasonTypeError || reasonError;
  const isFormDisabled = isBusy || !current;

  const handleDismiss = () => {
    dismissFlashMessage();
    onDismiss();
  };

  const handlePreview = async () => {
    setHasSubmitted(true);
    if (firstError) return;
    dismissFlashMessage();
    try {
      setPreview(await whileBusy(onPreview(responseDeadline)));
    } catch {
      // The flash message in the modal shows why.
    }
  };

  const handleConfirm = async () => {
    if (!preview || !reasonType || isBusy) return;
    await whileBusy(
      onConfirm({
        // The date the admin chose: reading the returned timestamp in the browser's timezone could move it a day.
        responseDeadline,
        reasonType,
        reason: trimmedReason,
      }),
    );
  };

  const handleGoBackToForm = () => {
    dismissFlashMessage();
    setPreview(undefined);
  };

  const flashMessage = flashMessagePayload ? (
    <FlashMessage payload={flashMessagePayload} onDismiss={dismissFlashMessage} timeRemaining={FLASH_MESSAGE_TIMEOUT} />
  ) : null;

  if (preview && reasonType) {
    const reversal = preview.autoResponseReversal;
    const reversalNotes = reversal
      ? [
          reversal.claimItemsUnsettled ? `${reversal.claimItemsUnsettled} claim item(s) unsettled` : null,
          reversal.finalAmountCleared !== undefined ? `final amount ${fmt(reversal.finalAmountCleared)} cleared` : null,
          reversal.substantiationDeadlineCleared ? 'substantiation deadline cleared' : null,
          reversal.claimStatusesRemoved ? `${reversal.claimStatusesRemoved} status record(s) since the agreement removed` : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : '';

    return (
      <Modal onDismiss={isBusy ? undefined : handleDismiss} capHeight height={MODAL_HEIGHT} maxWidth={MODAL_MAX_WIDTH}>
        <FixedModalLayout>
          {flashMessage}
          <Header3 style={{ marginBottom: 4 }}>Extend Response Deadline</Header3>
          <P2 style={{ marginBottom: 20 }}>Review the changes below before confirming.</P2>

          <ScrollableContent>
            <SectionDivider>
              <SectionDividerText>Modified</SectionDividerText>
            </SectionDivider>
            {renderSummaryCard(
              'Deadline',
              renderChangedValue(formatDate(preview.currentResponseDeadline), formatDate(preview.responseDeadline)),
              `${proposalLabel(preview)} · ${AWAITING_TENANT_RESPONSE}`,
            )}

            {reversal ? (
              <>
                {renderSummaryCard(
                  'Proposal',
                  renderChangedValue('Awaiting Payment', 'Awaiting Response'),
                  `Tenant auto-response undone · it runs again if they do not respond by ${formatDate(preview.responseDeadline)}`,
                )}
                {renderSummaryCard(
                  'Claim',
                  renderChangedValue(formatEnum(reversal.claimStatus), formatEnum(reversal.restoredClaimStatus)),
                  reversalNotes || undefined,
                )}

                {reversal.claimOrderId ? (
                  <>
                    <SectionDivider deletion>
                      <SectionDividerText deletion>Cancelled</SectionDividerText>
                    </SectionDivider>
                    {renderSummaryCard(
                      'Order',
                      reversal.claimOrderId,
                      `Claim order · ${reversal.autoChargeSchedulesCancelled} auto charge schedule(s) cancelled`,
                      {
                        deleted: true,
                        amount: reversal.claimOrderAmount !== undefined ? fmt(reversal.claimOrderAmount) : undefined,
                      },
                    )}
                  </>
                ) : null}
              </>
            ) : null}
          </ScrollableContent>

          <ActionBar>
            <Button buttonType="secondary" onClick={handleGoBackToForm} disabled={isBusy} noArrow>
              Go back
            </Button>
            <Button onClick={handleConfirm} disabled={isBusy}>
              Confirm
            </Button>
          </ActionBar>
        </FixedModalLayout>
      </Modal>
    );
  }

  return (
    <Modal onDismiss={isBusy ? undefined : handleDismiss} capHeight height={MODAL_HEIGHT} maxWidth={MODAL_MAX_WIDTH}>
      <FixedModalLayout>
        <ScrollableContent>
          {flashMessage}
          <Header3 style={{ marginBottom: 16 }}>Extend Response Deadline</Header3>

          {current ? (
            renderSummaryCard(
              'Proposal',
              proposalLabel(current),
              current.autoResponseReversal
                ? `${AWAITING_TENANT_RESPONSE} · the tenant was auto-responded, so extending will reverse it`
                : AWAITING_TENANT_RESPONSE,
            )
          ) : (
            <P2>{loadError ? `The response deadline cannot be extended: ${loadError}` : 'Loading the current deadline…'}</P2>
          )}

          <Divider />

          <SectionTitle>New deadline</SectionTitle>
          <VisuallyHiddenLabel htmlFor="claim-response-deadline-extension-date">New response deadline</VisuallyHiddenLabel>
          <DateChangeRow>
            <CurrentDateValue>{current ? formatDate(current.currentResponseDeadline) : '-'}</CurrentDateValue>
            <DateChangeArrow>→</DateChangeArrow>
            <DateInputWrapper>
              <DateInput
                disabled={isFormDisabled}
                id="claim-response-deadline-extension-date"
                min={moment().format('YYYY-MM-DD')}
                onChange={(event) => setResponseDeadline(event.target.value)}
                type="date"
                value={responseDeadline}
              />
            </DateInputWrapper>
          </DateChangeRow>
          {hasSubmitted && dateError ? <ValidationError>{dateError}</ValidationError> : null}

          <Divider />

          <SectionTitle>Reason</SectionTitle>
          <SectionText>Recorded with your name and shown on the claim.</SectionText>
          <Select name="reasonType" aria-label="Reason" value={reasonType} onChange={setReasonType} disabled={isFormDisabled}>
            <option value="">Select a reason</option>
            {REASON_TYPES.map((type) => (
              <option key={type} value={type}>
                {RESPONSE_DEADLINE_EXTENSION_REASON_LABELS[type]}
              </option>
            ))}
          </Select>
          {hasSubmitted && reasonTypeError ? <ValidationError>{reasonTypeError}</ValidationError> : null}
          <FieldGroup>
            <TextArea
              name="reason"
              value={reason}
              placeholder={reasonType ? RESPONSE_DEADLINE_EXTENSION_REASON_PLACEHOLDERS[reasonType] : 'Details'}
              onChange={(event) => setReason(event.target.value)}
              onBlur={() => undefined}
              disabled={isFormDisabled}
            />
          </FieldGroup>
          {hasSubmitted && reasonError ? <ValidationError>{reasonError}</ValidationError> : null}
        </ScrollableContent>

        <ActionBar>
          <Button buttonType="secondary" onClick={handleDismiss} disabled={isBusy} noArrow>
            Go back
          </Button>
          <DisabledActionTooltip title={hasSubmitted ? firstError : undefined}>
            <Button onClick={handlePreview} disabled={isFormDisabled}>
              Preview extension
            </Button>
          </DisabledActionTooltip>
        </ActionBar>
      </FixedModalLayout>
    </Modal>
  );
};
